import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { PaginationType, useCustomMutation } from '@codes/shared';

import { BaseApi, RequestConfig } from '../api';
import { createQueryKeys } from '../consts';

/**
 * Creates a set of React Query hooks for a specific resource
 * @template TView - Type for API response data
 * @template TBody - Type for API request body
 * @template TUpdateDTO - Type for update request DTO (must include id field)
 * @param resourceApi - Instance of BaseApi for the resource
 * @param queryKeys - Query key generators for the resource
 * @returns Object containing CRUD operation hooks
 *
 * @example
 * const {
 *   useList: useProjects,
 *   useInfiniteList: useInfiniteProjects
 *   useDetail: useProject,
 *   useCreate: useCreateProject,
 *   useUpdate: useUpdateProject,
 *   useDelete: useDeleteProject,
 * } = createResourceHooks<ProjectView, ProjectBody, UpdateProjectDTO>(projectApi, projectQueryKeys);
 */
export function createResourceHooks<TView, TBody, TUpdateDTO extends { id: string }>(
    resourceApi: BaseApi<TView, TBody>,
    queryKeys: ReturnType<typeof createQueryKeys>
) {
    /**
     * Hook for fetching a list of resources
     * @param config - API request configuration (optional)
     */
    const useList = (config?: RequestConfig) =>
        useQuery<PaginationType<TView[]>>({
            queryKey: queryKeys.list(config?.params ?? {}),
            queryFn: async () => {
                const result = await resourceApi.fetchList(config);
                return { ...result, data: result.list || [] } as PaginationType<TView[]>;
            },
            refetchOnWindowFocus: false,
            retry: 1,
        });

    /**
     * Hook for infinite scrolling list of resources
     * @param config - API request configuration (optional)
     */
    const useInfiniteList = (config?: RequestConfig) =>
        useInfiniteQuery({
            queryKey: queryKeys.list(config?.params ?? {}),
            queryFn: ({ pageParam = 0 }) =>
                resourceApi.fetchList({
                    ...config,
                    params: {
                        ...config?.params,
                        page: pageParam,
                    },
                }),
            getNextPageParam: lastPage => {
                const { page, limit, total } = lastPage;
                const maxPages = Math.ceil(total / limit);
                return page + 1 < maxPages ? page + 1 : undefined;
            },
            initialPageParam: 0,
        });

    /**
     * Hook for fetching a single resource by ID
     * @param id - Resource identifier
     * @param config - API request configuration (optional)
     */
    const useDetail = (id: string, config?: RequestConfig) =>
        useQuery<TView>({
            queryKey: queryKeys.detail(id),
            queryFn: () => resourceApi.fetchById(id, config),
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            enabled: !!id,
            retry: 1,
        });

    /**
     * Hook for creating a new resource
     */
    const useCreate = () => {
        return useCustomMutation(
            (requestConfig: RequestConfig & { body: TBody }) => resourceApi.create(requestConfig),
            {
                onError: error => {
                    toast({ title: `${error.toString()}` });
                },
            }
        );
    };

    /**
     * Hook for updating a resource
     */
    const useUpdate = () => {
        return useCustomMutation(
            (requestConfig: RequestConfig & { id: string }) => resourceApi.update(requestConfig.id, requestConfig),
            {
                onError: error => {
                    toast({ title: `${error.toString()}` });
                },
            }
        );
    };

    /**
     * Hook for deleting a resource
     */
    const useDelete = () => {
        return useCustomMutation(
            (requestConfig: RequestConfig & { id: string }) => resourceApi.delete(requestConfig.id, requestConfig),
            {
                onError: error => {
                    toast({ title: `${error.toString()}` });
                },
            }
        );
    };

    return {
        useList,
        useInfiniteList,
        useDetail,
        useCreate,
        useUpdate,
        useDelete,
    };
}
